
import BaseButton from 'JS/components/BaseButton.vue';
import CalendarDate from './CalendarDate.vue';
import Icon from 'JS/components/Icon.vue';
import { addDays, addMonths, addWeeks, format, isSameDay, startOfMonth, subDays, subMonths, subWeeks } from 'date-fns/fp';
import { chunks } from 'JS/utilities/array';
import { compareAsc, startOfDay } from 'date-fns';
import { computed, defineComponent, nextTick, PropType, ref, watch } from 'vue';
import { createDateLookup, DateLookup, getCalendarContainingDate } from 'JS/automotive/auto-service-scheduler/utils/calendar';
import { v4 as GenUUID } from 'uuid';

const keyboardKeyToNextActiveDateGetter: Readonly<Record<string, (date: Date) => Date>> = Object.freeze({
	ArrowDown: addWeeks(1),
	ArrowUp: subWeeks(1),
	ArrowLeft: subDays(1),
	ArrowRight: addDays(1),
});

export default defineComponent({
	components: {
		BaseButton,
		CalendarDate,
		Icon,
	},
	model: {
		// match Vue 3 v-model
		prop: 'modelValue',
		event: 'update:modelValue',
	},
	props: {
		modelValue: {
			type: Date as PropType<Date | null>,
		},
		availableDates: {
			type: Object as PropType<DateLookup>,
			required: true,
		},
		hideTodayLabel: {
			type: Boolean,
			default: false
		},
		size: {
			type: String as PropType<'sm' | 'lg' | 'responsive'>,
			default: 'responsive'
		},
		accessibleCalendarName: {
			type: String,
			required: true,
		},
		accessibleCalendarDescription: {
			type: String,
			required: true,
		},
	},
	setup(props, { emit }) {
		const uuid = GenUUID();

		const activeDate = ref(props.modelValue || startOfDay(new Date()));
		const referenceDateForMonth = ref(startOfMonth(props.modelValue || new Date()));
		const calendar = computed(() => getCalendarContainingDate(referenceDateForMonth.value));

		const weeks = computed(() => chunks(7, calendar.value));

		const nextMonth = computed(() => startOfMonth(addMonths(1, referenceDateForMonth.value)));
		const previousMonth = computed(() => startOfMonth(subMonths(1, referenceDateForMonth.value)));

		const availableDateArray = computed(() => props.availableDates.values);

		const minMonth = computed(() => {
			const firstAvailableDate = availableDateArray.value[0] ?? new Date();
			return startOfMonth(firstAvailableDate);
		});

		const maxMonth = computed(() => {
			const lastAvailableDate = availableDateArray.value[availableDateArray.value.length - 1] ?? new Date();
			return startOfMonth(lastAvailableDate);
		});

		const canGoToPreviousMonth = computed(() => compareAsc(previousMonth.value, minMonth.value) !== -1);
		const canGoToNextMonth = computed(() => compareAsc(nextMonth.value, maxMonth.value) !== 1);


		function isActive(date: Date): boolean {
			return isSameDay(date, activeDate.value);
		}

		function isDisabled(day: Date): boolean {
			return !props.availableDates.has(day);
		}

		function goToNextMonth() {
			if (canGoToNextMonth.value) {
				referenceDateForMonth.value = nextMonth.value;
			}
		}

		function goToPreviousMonth() {
			if (canGoToPreviousMonth.value) {
				referenceDateForMonth.value = previousMonth.value;
			}
		}

		function handleDateKeydown(e: KeyboardEvent, date: Date) {
			const getNextActiveDate = keyboardKeyToNextActiveDateGetter[e.key];
			if (getNextActiveDate) {
				e.preventDefault();
				const nextDate = getNextActiveDate(date);

				if (nextDate > calendar.value[calendar.value.length - 1]) {
					goToNextMonth();
				} else if (nextDate < calendar.value[0]) {
					goToPreviousMonth();
				}

				activeDate.value = nextDate;
				nextTick(() => {
					const el = document.querySelector<HTMLElement>(`button[data-datestring="${CSS.escape(nextDate.toDateString().replace(/\s+/g, ''))}"]`);
					el?.focus();
				});
			}
		}

		const calendarLookup = computed(() => createDateLookup(calendar.value));
		// ensures that there's a focusable date when moving between months
		watch(referenceDateForMonth, refDate => {
			if (!calendarLookup.value.has(activeDate.value)) {
				activeDate.value = (props.modelValue && calendarLookup.value.has(props.modelValue))
					? props.modelValue
					: refDate;
			}
		});

		// ensures referenceDateForMonth is between min and max when they've changed (like after new available dates load)
		watch([minMonth, maxMonth, referenceDateForMonth], ([min, max, referenceDate]) => {
			if (referenceDate < min) {
				referenceDateForMonth.value = min;
			} else if (referenceDate > max) {
				referenceDateForMonth.value = max;
			}
		});



		return {
			uuid,
			activeDate,
			weeks,
			canGoToNextMonth,
			goToNextMonth,
			canGoToPreviousMonth,
			goToPreviousMonth,
			handleDateKeydown,
			headerLabel: computed(() => format('MMMM Y', referenceDateForMonth.value)),
			isSelected: (date: Date) => !!props.modelValue && isSameDay(props.modelValue, date),
			isActive,
			isDisabled,
			isToday: isSameDay(new Date),
			selectDate(date: Date) {
				if (!props.availableDates.has(date)) {
					return;
				}

				emit('update:modelValue', date);
				activeDate.value = date;
			},
			numberOfAvailableDatesLabel: computed(() => {
				const numAvailableInMonth = calendar.value.filter(d => props.availableDates.has(d)).length;
				return `${numAvailableInMonth} appointment date${numAvailableInMonth === 1 ? '' : 's'} available`;
			})
		};
	}
});

